import React, { useState, useEffect } from 'react';
import UserApi from '../../api/platform/userApi';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import styles from '../../css/setting/settings-content.module.css';
import { UserModel } from '../../models/platform/userModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import {
  handleError,
  mapResponseErrorToSystemError,
  mapUnknownErrorToSystemError
} from '../../utils/errorUtils';

const UserSetting: React.FC = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await UserApi.getUser();
        if (response.success) {
          const data = (response as ResponseSuccessModel).data.user as UserModel;
          setEmail(data.email || '');
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setTitle(data.title || '');
          setPhoneNumber(data.phone_number || '');
        } else {
          handleError(
            mapResponseErrorToSystemError(response as ResponseErrorModel),
            setErrorMessage,
            () => { },
            triggerSystemError
          );
        }
      } catch (error) {
        handleError(
          mapUnknownErrorToSystemError(error, false),
          setErrorMessage,
          () => { },
          triggerSystemError
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [triggerSystemError]);

  const handleSave = async () => {
    if (!firstName || !lastName || !title || !phoneNumber) {
      setErrorMessage('Please fill in all fields.');
      return;
    }

    try {
      const response = await UserApi.updateUser(firstName, lastName, title, phoneNumber);
      if (response.success) {
        setSuccessMessage('User info updated successfully!');
        setErrorMessage(null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          setErrorMessage,
          () => { },
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        setErrorMessage,
        () => { },
        triggerSystemError
      );
    }
  };

  return (
    <div className={styles.settingsContainer}>
      {loading ? (
        <div className={styles.spinner}>Loading...</div>
      ) : (
        <form
          className={styles.formLayout}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className={styles.formGroup}>
            <label>Email:</label>
            <div className={styles.formStaticValue}>{email}</div>
          </div>

          <div className={styles.formGroup}>
            <label>First Name:</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Last Name:</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Phone Number:</label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>

          <button type="submit" className={styles.submitButton}>Save</button>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
        </form>
      )}
    </div>

  );
};

export default UserSetting;
